import React, { useEffect, useState } from "react";
import AppHeader from "../../../components/layout/AppHeader";
import PageHeader from "../../../components/common/PageHeader";
import { getEmployeeCount, getEmployeeProfiles } from "../api/hrService";

import TableWithFilter from "../../../components/table/TableWithFilter";
import FilterPanel from "../../../components/table/FilterPanel";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import {
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";


import "../styles/HRList.css";
import { getVerticalList } from "../../contact/api/contactService";
import { toast } from "react-toastify";

interface User {
  id: number;
  user: number;               // employee PK
  employee_id: string | null; // employee code
  first_name: string | null;
  last_name: string | null;
  mobile: string | null;
  is_active: string;
  role?: string[];
  vertical?: string[];  // ✅ change
}


const HRList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  // const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [openActionId, setOpenActionId] = useState<number | null>(null);
  const navigate = useNavigate();
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [prevPage, setPrevPage] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState<number | null>(null);
  const [ordering, setOrdering] = useState<string>("");
  const isFirstLoad = React.useRef(true);
  const hasLoadedOnce = React.useRef(false);
  const [summary, setSummary] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const getSortIcon = (field: string) => {
    if (ordering === field) return faSortUp;
    if (ordering === `-${field}`) return faSortDown;
    return faSort;
  };
  const [verticalOptions, setVerticalOptions] = useState<any[]>([]);

  useEffect(() => {
    fetchVerticals();
  }, []);

  const fetchVerticals = async () => {
    try {
      const res = await getVerticalList();

      setVerticalOptions(
  res.data.results.map((v: any) => ({
    label: v.name,
    value: v.name,   // ✅ USE NAME INSTEAD OF ID
  }))
);
    } catch {
      toast.error("Failed to load verticals");
    }
  };
  const ORDER_FIELDS = [
    "id",
    "first_name",
    "last_name",
    "date_created",
    "is_active",
  ];

  const toggleOrdering = (field: string) => {
    setOrdering(prev => {
      if (prev === field) return `-${field}`;        // ASC → DESC
      if (prev === `-${field}`) return "";           // DESC → NONE
      return field;                                  // NONE → ASC
    });
  };

  const [filters, setFilters] = useState({
    id: "",
    first_name: "",
    last_name: "",
    mobile: "",
    role: "",
    vertical: "",
    status: "",
  });

  useEffect(() => {
    fetchEmployees(page, filters);
  }, [page, ordering]);

  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return; // 🚀 skip first mount
    }

    const handler = setTimeout(() => {
      setPage(1);
      fetchEmployees(1, filters);
    }, 500);

    return () => clearTimeout(handler);
  }, [filters]);
  const fetchEmployeeCount = async () => {
    try {
      const res = await getEmployeeCount();

      setSummary({
        total: res.data.total,
        active: res.data.active,
        inactive: res.data.inactive,
      });
    } catch {
      console.error("Failed to load employee summary");
    }
  };

  useEffect(() => {
    fetchEmployeeCount();
  }, []);


  const fetchEmployees = async (pageNo: number, appliedFilters = filters) => {
    if (hasLoadedOnce.current) {
      setLoading(true);
    }


    try {
      const res = await getEmployeeProfiles(pageNo, {
        id: appliedFilters.id,
        first_name: appliedFilters.first_name,
        last_name: appliedFilters.last_name,
        mobile: appliedFilters.mobile,
        role: appliedFilters.role,
        vertical: appliedFilters.vertical || undefined,
        is_active: appliedFilters.status,
        ordering, // ✅ HERE
      });

      if (res.data.results) {
        setUsers(res.data.results);
      }
      setTotalCount(res.data.count || 0);
      const list = res.data.results || [];


      setNextPage(res.data.next);
      setPrevPage(res.data.previous);

      if (pageSize === null && res.data.results?.length) {
        setPageSize(res.data.results.length);
      }
    } finally {
      setLoading(false);

    }
  };

  const totalPages =
    pageSize && totalCount
      ? Math.ceil(totalCount / pageSize)
      : 1;

  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [totalPages]);


  useEffect(() => {
    const handleClickOutside = () => setOpenActionId(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);




  return (
    <>

      <PageHeader
        title="Employee Master"
        breadcrumbs={[
          { label: "Dashboard", path: "/dashboard" },
          { label: "HR" },
          { label: "Employee Master" },
        ]}
      />

      <div className="container mt-4 position-relative">
        <div className="stats-row hr-stats mb-3">
          <StatCard color="blue" icon="fa-users" title="Total Employees" value={summary.total} />
          <StatCard color="green" icon="fa-user-check" title="Active Employees" value={summary.active} />
          <StatCard color="red" icon="fa-user-times" title="Inactive Employees" value={summary.inactive} />
        </div>

        <div className="d-flex align-items-center justify-content-end mb-4">
          <button
            className="btn btn-primary"
            onClick={() => navigate("/hr/create-user")}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Employee
          </button>
        </div>
        {/* <TableWithFilter filterPanel={<FilterPanel />}> */}

        <div className="hr-card">


          <>
            {loading && (
              <div className="table-loading-overlay">
                <span className="spinner-border spinner-border-sm" />
              </div>
            )}

            <div className="table-wrapper position-relative">

              <table className="table hr-table">
                <thead>
                  <tr>
                    {/* <th>#</th> */}
                    <th style={{ textAlign: "center" }}>Action</th>
                    {/* <th onClick={() => toggleOrdering("id")} className="sortable">
                      Employee Code <FontAwesomeIcon icon={getSortIcon("id")} />
                    </th> */}

                    <th onClick={() => toggleOrdering("first_name")} className="sortable">
                      First Name{" "}
                      <FontAwesomeIcon icon={getSortIcon("first_name")} />
                    </th>

                    <th onClick={() => toggleOrdering("last_name")} className="sortable">
                      Last Name <FontAwesomeIcon icon={getSortIcon("last_name")} />
                    </th>
                    <th>Mobile</th>

                    <th onClick={() => toggleOrdering("role")} className="sortable">
                      Role <FontAwesomeIcon icon={getSortIcon("role")} />
                    </th>
                    <th>Vertical</th>
                    <th onClick={() => toggleOrdering("is_active")} className="sortable">
                      Status <FontAwesomeIcon icon={getSortIcon("is_active")} />
                    </th>


                  </tr>
                  <tr className="search-row">
                    <th></th>

                    {/* <th>
                      <input
                        className="form-control form-control-sm"
                        placeholder="Search "
                        value={filters.id}
                        onChange={(e) => {
                          const val = e.target.value;

                          // ✅ allow empty (reset)
                          if (val === "") {
                            setFilters({ ...filters, id: "" });
                            return;
                          }

                          // ❌ block alphabets
                          if (!/^\d+$/.test(val)) {
                            return;
                          }

                          setFilters({ ...filters, id: val });
                        }}
                      />

                    </th> */}

                    <th>
                      <input
                        className="form-control form-control-sm"
                        placeholder="Search "
                        value={filters.first_name}
                        onChange={(e) =>
                          setFilters({ ...filters, first_name: e.target.value })
                        }
                      />
                    </th>

                    <th>
                      <input
                        className="form-control form-control-sm"
                        placeholder="Search"
                        value={filters.last_name}
                        onChange={(e) =>
                          setFilters({ ...filters, last_name: e.target.value })
                        }
                      />
                    </th>

                    <th>
                      <input
                        className="form-control form-control-sm"
                        placeholder="Search"
                        value={filters.mobile}
                        onChange={(e) =>
                          setFilters({ ...filters, mobile: e.target.value })
                        }
                      />
                    </th>
                    <th>
                      <input
                        className="form-control form-control-sm"
                        placeholder="Search"
                        value={filters.role}
                        onChange={(e) =>
                          setFilters({ ...filters, role: e.target.value })
                        }
                      />
                    </th>
                    <th>
                    <select
  className="form-select form-select-sm"
  value={filters.vertical}
  onChange={(e) =>
    setFilters((prev) => ({
      ...prev,
      vertical: e.target.value, // now sends "Glazing"
    }))
  }
>
  <option value="">All</option>
  {verticalOptions.map((v) => (
    <option key={v.value} value={v.value}>
      {v.label}
    </option>
  ))}
</select>
                    </th>

                    <th>
                      <select
                        className="form-select form-select-sm"
                        value={filters.status}
                        onChange={(e) =>
                          setFilters({ ...filters, status: e.target.value })
                        }
                      >
                        <option value="">All</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>

                    </th>
                  </tr>

                </thead>

                <tbody>
                  {users.length === 0 && !loading ? (
                    <tr>
                      <td colSpan={7} className="text-center">
                        No employees found
                      </td>
                    </tr>
                  ) : (

                    users.map((u, idx) => {
                      // console.log("Row data:", u); // 👈 ADD HERE (TEMP)

                      return (

                        <tr key={u.id}>
                          {/* <td>{(page - 1) * PAGE_SIZE + idx + 1}</td> */}

                          <td style={{ textAlign: "center", position: "relative" }}>
                            <button
                              type="button"
                              className="action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenActionId(openActionId === u.id ? null : u.id);
                              }}
                            >
                              <FontAwesomeIcon icon={faEllipsisV} />
                            </button>

                            {openActionId === u.id && (
                              <div
                                className="action-dropdown"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {/* VIEW */}
                                <button
                                  type="button"
                                  className="view"
                                  onClick={() => {
                                    setOpenActionId(null);
                                    navigate(`/hr/view/${u.user}`);   // ✅ PASS USER ID
                                  }}
                                >
                                  View
                                </button>

                                {/* EDIT */}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpenActionId(null);
                                    navigate(`/hr/edit/${u.user}`);   // ✅ PASS USER ID
                                  }}
                                >
                                  Edit
                                </button>
                              </div>
                            )}
                          </td>


                          {/* <td>{u.id || "-"}</td> */}
                          <td>{u.first_name || "-"}</td>
                          <td>{u.last_name || "-"}</td>
                          <td>{u.mobile || "-"}</td>
                          <td> {u.role?.length
                            ? u.role.join(", ")
                            : "-"}</td>
                          <td>
                            {u.vertical?.length
                              ? u.vertical.join(", ")
                              : "-"}
                          </td>
                          <td>

                            <span
                              className={`status-badge ${u.is_active === "Active"
                                ? "active"
                                : "inactive"
                                }`}
                            >
                              {u.is_active}
                            </span>
                          </td>
                        </tr>)
                    })
                  )}
                </tbody>
              </table>
            </div>
            {/* PAGINATION */}
            <div className="pagination d-flex gap-1 justify-content-end">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={!prevPage}
                onClick={() => setPage(page - 1)}
              >
                Prev
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  className={`btn btn-sm ${p === page ? "btn-primary" : "btn-outline-secondary"
                    }`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              ))}

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={!nextPage}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div></>

        </div>
        {/* </TableWithFilter> */}
      </div>
    </>
  );
};

export default HRList;
const StatCard = ({ color, icon, title, value }: any) => (
  <div className={`stat-card ${color}`}>
    <div className="stat-icon">
      <i className={`fas ${icon}`}></i>
    </div>
    <div>
      <div className="stat-title">{title}</div>
      <div className="stat-value">{value || 0}</div>
    </div>
  </div>
);
